import { BlogEditorLeftToolBarActionEnum } from '@doltech/utils/lib/constants';
import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { values } from 'lodash';
import { getRoot, types as t } from 'mobx-state-tree';
import { nanoid } from 'nanoid';
import { Transforms } from 'slate';

import { BlogEditorSelectStateStoreInstance } from './blog-editor-select-state.store';
import { SAMPLE_TYPE } from './plugins/SpecialObject/IeltsSample/types';
import { ELEMENT_IMAGE_SLIDE_SHOW } from './plugins/SpecialObject/ImageSlide/SlideShow/ImageSlideShowPlugin.types';
import {
  ELEMENT_IMAGE_SLIDE_TEXT,
  ELEMENT_IMAGE_SLIDE_TEXT_INPUT,
} from './plugins/SpecialObject/ImageSlide/SlideText/ImageSlideTextPlugin.types';

export const BlogEditorLeftSidebarStore = t
  .model('BlogEditorLeftSidebarStore', {
    menuEnabled: t.optional(t.array(t.string), values(BlogEditorLeftToolBarActionEnum)),
  })
  .views((self) => ({
    get menuEnabledBasedOnSelection() {
      const rootStore: any = getRoot(self);
      const selectState: BlogEditorSelectStateStoreInstance = rootStore?.selectState;
      if (
        (selectState && selectState.isInCardLogic) ||
        selectState.isInTable ||
        selectState.isInSpecialObject ||
        selectState.isInSampleSpecialObject ||
        selectState.isInBlankV2 ||
        selectState.isInExerciseGapFill ||
        selectState.isInExerciseMultipleChoice ||
        selectState.isInExerciseShortAnswer ||
        selectState.isInPluginExplanation ||
        selectState.isHeading2 ||
        selectState.isHeading3
      ) {
        return [];
      }
      if (!selectState.isTextSelection) {
        return values(BlogEditorLeftToolBarActionEnum);
      }
      if ((selectState && selectState.isInBulletPoint) || selectState.isInQuoteBlock) {
        const { isTextSelection, isInBorderShading } = selectState;
        return [
          isTextSelection && !isInBorderShading
            ? BlogEditorLeftToolBarActionEnum.BORDER_AND_SHADING
            : null,
        ];
      }
      if (selectState && selectState.isInBorderShading) {
        return [BlogEditorLeftToolBarActionEnum.IMAGE];
      }
      return values(BlogEditorLeftToolBarActionEnum);
    },
  }))
  .views((self) => ({
    getInsertActionDisabledByType(type) {
      return !self.menuEnabledBasedOnSelection.includes(type);
    },
  }))
  .actions(() => ({
    async insertNewElementByType({
      type,
      editor,
    }: {
      type: BlogEditorLeftToolBarActionEnum;
      editor: any;
    }) {
      switch (type) {
        case BlogEditorLeftToolBarActionEnum.CARD_LOGIC: {
          const { CardLogicTransformActions } = await import(
            './plugins/CardLogic/model/transforms'
          );

          CardLogicTransformActions.addNewCardLogic(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.TABLE: {
          const { addTable } = await import('../DolEditor/elements/table-blog/transforms/addTable');
          addTable(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.BORDER_AND_SHADING: {
          const { addBorderShading } = await import(
            '../DolEditor/elements/border-shading/transform/addBorderShading'
          );

          addBorderShading(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IMAGE:
        case BlogEditorLeftToolBarActionEnum.VIDEO:
        case BlogEditorLeftToolBarActionEnum.FRAME:
        case BlogEditorLeftToolBarActionEnum.FILE:
        case BlogEditorLeftToolBarActionEnum.AUDIO:
        case BlogEditorLeftToolBarActionEnum.CALCULATOR:
        case BlogEditorLeftToolBarActionEnum.CONVERTER:
        case BlogEditorLeftToolBarActionEnum.VOCAB_LIST:
        case BlogEditorLeftToolBarActionEnum.VOCAB_LIST_NEWS:
        case BlogEditorLeftToolBarActionEnum.PAPER_EXAM: {
          const { addMedia } = await import('../DolEditor/elements/table-blog/transforms/addTable');
          addMedia(editor, type);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.VOCAB_CARD: {
          const { addVocabCard } = await import(
            '../DolEditor/elements/table-blog/transforms/addTable'
          );
          addVocabCard(editor, BlogEditorLeftToolBarActionEnum.VOCAB_CARD);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.DIVIDER: {
          const { addDivider } = await import('../DolEditor/elements/divider/transform/addDivider');
          addDivider(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.BOOK_REVIEW: {
          const { addSpecialObject } = await import(
            './plugins/SpecialObject/transforms/addSpecialObject'
          );
          addSpecialObject(editor, 'book');
          break;
        }
        case BlogEditorLeftToolBarActionEnum.COURSE_REVIEW: {
          const { addSpecialObject } = await import(
            './plugins/SpecialObject/transforms/addSpecialObject'
          );

          addSpecialObject(editor, 'course');
          break;
        }
        case BlogEditorLeftToolBarActionEnum.TEACHER_REVIEW: {
          const { addSpecialObject } = await import(
            './plugins/SpecialObject/transforms/addSpecialObject'
          );

          addSpecialObject(editor, 'teacher');
          break;
        }
        case BlogEditorLeftToolBarActionEnum.CENTER_REVIEW: {
          const { addSpecialObject } = await import(
            './plugins/SpecialObject/transforms/addSpecialObject'
          );

          addSpecialObject(editor, 'center');
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_SAMPLE_W1: {
          const { addSampleSpecialObject } = await import(
            './plugins/SpecialObject/IeltsSample/transforms/addSampleSpecialObject'
          );

          addSampleSpecialObject(editor, SAMPLE_TYPE.W1);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_SAMPLE_W2A: {
          const { addSampleSpecialObject } = await import(
            './plugins/SpecialObject/IeltsSample/transforms/addSampleSpecialObject'
          );

          addSampleSpecialObject(editor, SAMPLE_TYPE.W2A);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_SAMPLE_W2G: {
          const { addSampleSpecialObject } = await import(
            './plugins/SpecialObject/IeltsSample/transforms/addSampleSpecialObject'
          );

          addSampleSpecialObject(editor, SAMPLE_TYPE.W2G);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_SAMPLE_S1: {
          const { addSampleSpecialObject } = await import(
            './plugins/SpecialObject/IeltsSample/transforms/addSampleSpecialObject'
          );

          addSampleSpecialObject(editor, SAMPLE_TYPE.S1);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_SAMPLE_S2: {
          const { addSampleSpecialObject } = await import(
            './plugins/SpecialObject/IeltsSample/transforms/addSampleSpecialObject'
          );

          addSampleSpecialObject(editor, SAMPLE_TYPE.S2);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_SAMPLE_S3: {
          const { addSampleSpecialObject } = await import(
            './plugins/SpecialObject/IeltsSample/transforms/addSampleSpecialObject'
          );

          addSampleSpecialObject(editor, SAMPLE_TYPE.S3);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.EXERCISE_MULTIPLE_CHOICE: {
          const { addExerciseMultipleChoice } = await import(
            './plugins/ExerciseMultipleChoice/transforms/addExerciseMultipleChoice'
          );

          addExerciseMultipleChoice(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.EXERCISE_GAP_FILL: {
          const { addExerciseGapFill } = await import(
            './plugins/GapFill/transforms/addExerciseGapFill'
          );

          addExerciseGapFill(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.EXERCISE_SHORT_ANSWER: {
          const { addExerciseShortAnswer } = await import(
            './plugins/ExerciseShortAnswer/transforms/addExerciseShortAnswer'
          );

          addExerciseShortAnswer(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_CALENDAR_COMPUTER_BASED: {
          const { ieltsCalendarComputedBasedTransforms } = await import(
            './plugins/SpecialObject/IeltsCalendar/ComputerBased/transforms'
          );
          ieltsCalendarComputedBasedTransforms.addNode(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IELTS_PURPOSE_CONVERTER: {
          const { ieltsPurposeConverterTransforms } = await import(
            './plugins/SpecialObject/IeltsConverter/Purpose/transforms'
          );

          ieltsPurposeConverterTransforms.addNode(editor);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IMAGE_SLIDE_SHOW: {
          Transforms.insertNodes(editor, {
            type: ELEMENT_IMAGE_SLIDE_SHOW,
            isVoid: true,
            id: nanoid(),
            children: [
              {
                text: '',
              },
            ],
          } as any);
          break;
        }
        case BlogEditorLeftToolBarActionEnum.IMAGE_SLIDE_TEXT: {
          Transforms.insertNodes(editor, {
            type: ELEMENT_IMAGE_SLIDE_TEXT,
            id: nanoid(),
            children: [
              {
                type: ELEMENT_IMAGE_SLIDE_TEXT_INPUT,
                id: nanoid(),
                children: [getEmptyParagraphNode()],
              },
            ],
          } as any);
          break;
        }

        case BlogEditorLeftToolBarActionEnum.EXERCISE_GAP_FILL_WITH_HINT: {
          const { addExerciseGapFillWithHint } = await import(
            './plugins/ExerciseGapFillWithHint/transforms/gapFillWithHintAction'
          );

          addExerciseGapFillWithHint(editor);
          break;
        }

        case BlogEditorLeftToolBarActionEnum.QUIZ: {
          const { addExerciseQuiz } = await import(
            './plugins/ExerciseQuiz/transforms/exerciseQuizAction'
          );

          addExerciseQuiz(editor);
          break;
        }

        case BlogEditorLeftToolBarActionEnum.FAQ: {
          const { addFaq } = await import('./plugins/FAQ/transforms/faqAction');
          addFaq(editor);
          break;
        }
        default:
      }
    },
  }));
